import * as THREE from 'three';
import './styles.css';
import { AudioSystem } from './audio/audioSystem';
import { InputController } from './input/input';
import { MultiplayerClient, type RemoteMissileState } from './network/multiplayerClient';
import { AirCombatRenderer } from './render/airCombatRenderer';
import { GameSimulation } from './simulation/game';
import type { SimEvent } from './simulation/types';
import { formatMeters } from './utils/math';

const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
const simulation = new GameSimulation();
const renderer = new AirCombatRenderer(canvas, simulation);
const input = new InputController(canvas);
const multiplayer = new MultiplayerClient();
const audio = new AudioSystem();

const homeScreen = document.getElementById('home-screen') as HTMLElement;
const hangarScreen = document.getElementById('hangar-screen') as HTMLElement;
const settingsScreen = document.getElementById('settings-screen') as HTMLElement;
const pauseScreen = document.getElementById('pause-screen') as HTMLElement;
const gameOverScreen = document.getElementById('game-over-screen') as HTMLElement;
const hud = document.getElementById('hud') as HTMLElement;
const combatHints = document.getElementById('combat-hints') as HTMLElement;
const crosshair = document.getElementById('crosshair') as HTMLElement;
const combatDock = document.querySelector('.combat-dock') as HTMLElement;
const touchControls = document.getElementById('touch-controls') as HTMLElement;
const leaderboardPanel = document.getElementById('leaderboard-panel') as HTMLElement;
const leaderboardList = document.getElementById('leaderboard-list') as HTMLElement;
const chatPanel = document.getElementById('chat-panel') as HTMLElement;
const chatLog = document.getElementById('chat-log') as HTMLElement;
const chatHint = document.getElementById('chat-hint') as HTMLElement;
const chatInput = document.getElementById('chat-input') as HTMLInputElement;
const damageVignette = document.getElementById('damage-vignette') as HTMLElement;
const eventFeed = document.getElementById('event-feed') as HTMLElement;
const radarCanvas = document.getElementById('radar') as HTMLCanvasElement;
const radarCtx = radarCanvas.getContext('2d')!;
const targetBox = document.getElementById('target-box') as HTMLElement;
const targetName = document.getElementById('target-name') as HTMLElement;
const targetRange = document.getElementById('target-range') as HTMLElement;
const targetHealthWrap = document.getElementById('target-health') as HTMLElement | null;
const targetHealthLabel = document.getElementById('target-health-label') as HTMLElement | null;
const targetHealthFill = document.getElementById('target-health-fill') as HTMLElement | null;
const lockFill = document.getElementById('lock-fill') as HTMLElement;
const lockLabel = document.getElementById('lock-label') as HTMLElement;
const weaponPanel = document.querySelector('.weapon-panel') as HTMLElement;
const hudMode = document.getElementById('hud-mode') as HTMLElement;
const hudTargetCount = document.getElementById('hud-target-count') as HTMLElement;
const selfHealthLabel = document.getElementById('self-health-label') as HTMLElement | null;
const selfHealthFill = document.getElementById('self-health-fill') as HTMLElement | null;

const missionStatus = document.getElementById('mission-status') as HTMLElement;
const killBanner = document.getElementById('kill-banner') as HTMLElement;
const lockWarning = document.getElementById('lock-warning') as HTMLElement;
const hudSpeed = document.getElementById('hud-speed') as HTMLElement;
const hudAltitude = document.getElementById('hud-altitude') as HTMLElement;
const hudHealth = document.getElementById('hud-health') as HTMLElement;
const hudAmmo = document.getElementById('hud-ammo') as HTMLElement;
const hudFlares = document.getElementById('hud-flares') as HTMLElement;
const hudHeat = document.getElementById('hud-heat') as HTMLElement;
const hudScore = document.getElementById('hud-score') as HTMLElement;
const gameOverTitle = document.getElementById('game-over-title') as HTMLElement;
const gameOverSummary = document.getElementById('game-over-summary') as HTMLElement;
const gameOverKicker = document.getElementById('game-over-kicker') as HTMLElement;
const gameOverAircraftPicker = document.getElementById('game-over-aircraft-picker') as HTMLElement;

const startButton = document.getElementById('start-button') as HTMLButtonElement;
const startMpButton = document.getElementById('start-mp-button') as HTMLButtonElement;
const langZhButton = document.getElementById('lang-zh-button') as HTMLButtonElement;
const langEnButton = document.getElementById('lang-en-button') as HTMLButtonElement;
const settingsButton = document.getElementById('settings-button') as HTMLButtonElement;
const settingsReconnectButton = document.getElementById('settings-reconnect-button') as HTMLButtonElement;
const settingsCloseButton = document.getElementById('settings-close-button') as HTMLButtonElement;
const pauseReconnectButton = document.getElementById('pause-reconnect-button') as HTMLButtonElement;
const homeContinueButton = document.getElementById('home-continue-button') as HTMLButtonElement;
const hangarBackButton = document.getElementById('hangar-back-button') as HTMLButtonElement;
const resumeButton = document.getElementById('resume-button') as HTMLButtonElement;
const restartButton = document.getElementById('restart-button') as HTMLButtonElement;
const retryButton = document.getElementById('retry-button') as HTMLButtonElement;
const gameOverHomeButton = document.getElementById('game-over-home-button') as HTMLButtonElement;
const homePilotNameInput = document.getElementById('home-pilot-name-input') as HTMLInputElement;
const hangarPilotNameInput = document.getElementById('hangar-pilot-name-input') as HTMLInputElement;
const hangarAircraftStatus = document.getElementById('hangar-aircraft-status') as HTMLElement;
const aircraftCards = [...document.querySelectorAll<HTMLButtonElement>('[data-aircraft]')];
const missileButton = document.getElementById('btn-missile') as HTMLButtonElement;
const gunButton = document.getElementById('btn-gun') as HTMLButtonElement;
const flareButton = document.getElementById('btn-flare') as HTMLButtonElement;
const targetButton = document.getElementById('btn-target') as HTMLButtonElement;
const throttleDownButton = document.getElementById('btn-throttle-down') as HTMLButtonElement;
const throttleUpButton = document.getElementById('btn-throttle-up') as HTMLButtonElement;
const boostButton = document.getElementById('btn-boost') as HTMLButtonElement;
const pauseButton = document.getElementById('btn-pause') as HTMLButtonElement;
const sensitivityInput = document.getElementById('setting-sensitivity') as HTMLInputElement;
const sensitivityValue = document.getElementById('setting-sensitivity-value') as HTMLElement;
const invertYInput = document.getElementById('setting-invert-y') as HTMLInputElement;
const hudOpacityInput = document.getElementById('setting-hud-opacity') as HTMLInputElement;
const hudOpacityValue = document.getElementById('setting-hud-opacity-value') as HTMLElement;
const showFeedInput = document.getElementById('setting-show-feed') as HTMLInputElement;
const showHintsInput = document.getElementById('setting-show-hints') as HTMLInputElement;

let lastTime = performance.now();
let feed: Array<{ text: string; createdAt: number; tone: string }> = [];
let pulseDamage = 0;
let multiplayerMode = false;
let stateSendCooldown = 0;
let remoteTargetId: string | null = null;
let remoteLockProgress = 0;
let autoTrackLockedTarget = false;
let selectedAircraftId = 'j15';
let selectedPilotName = '';
let resumeAfterSettingsClose = false;
let multiplayerConnecting = false;
let multiplayerDeathShown = false;
let lockWarningTimer = 0;
let killBannerTimer = 0;
let chatTyping = false;
let chatMessages: Array<{ text: string; tone: string }> = [];
let pendingNetworkEvents: SimEvent[] = [];
type LeaderboardEntry = { id: string; name: string; score: number; self: boolean };

const LANGUAGE_STORAGE_KEY = 'hfs-sky-force-language';
const PILOT_NAME_STORAGE_KEY = 'hfs-sky-force-pilot-name';
const translations = {
  zh: {
    settingsEyebrow: '战术控制',
    settingsTitle: '设置',
    reconnectNode: '重连 HK 节点',
    close: '关闭',
    flightControlsTitle: '飞行控制',
    mouseSensitivity: '鼠标灵敏度',
    invertYAxis: '反转 Y 轴',
    mouseMoveKey: '拖拽屏幕',
    freeLookValue: '调整视角',
    pitchNoseValue: '俯仰机头',
    rollValue: '滚转',
    yawValue: '偏航',
    throttleKey: '滚轮',
    zoomValue: '视角缩放',
    cannonKey: 'C / GUN',
    throttleAdjustValue: '油门调整',
    afterburnerValue: '加力',
    hudTitle: '作战界面',
    hudOpacity: 'HUD 透明度',
    showFeed: '显示战术信息流',
    showHints: '显示操作提示',
    fireMissileValue: '发射导弹',
    lockRequirementKey: '先决条件',
    lockRequirementValue: '先用 Tab 选中目标并完成锁定',
    cannonValue: '机炮',
    flareValue: '释放热焰弹',
    cycleTargetValue: '切换锁定目标',
    pauseValue: '战术暂停',
    releaseNotes: 'Release Notes',
    changelogTitle: '更新记录',
    homeEyebrow: 'Hamster Flight Simulator',
    brandTitle: 'HFS: 天空部队',
    homeIntro: '录入呼号，进入机库，随后直接出击。',
    pilotName: '玩家昵称',
    enterHangar: '进入机库',
    hangarEyebrow: 'Hangar',
    hangarTitle: '机库配置',
    backHome: '返回首页',
    multiplayerRoom: '联机节点',
    multiplayerNote: '联机出击将直接接入 HK 节点空域。',
    singleplayerLaunch: '单机出击',
    multiplayerLaunch: '联机出击',
    multiplayerConnecting: '接入 HK 节点中…',
    homePlaceholder: '输入你的呼号',
    hangarPlaceholder: '呼号',
    combatStandingBy: '战斗待命',
    pausedEyebrow: '战斗暂停',
    pausedTitle: '重新编队后继续压制',
    resumeCombat: '继续战斗',
    restartSortie: '重新出击',
    missionEnd: '任务结束',
    baseLost: '基地失守',
    scoreZero: '得分 0',
    sortieAgain: '再次出击',
    missionComplete: '任务完成',
    missionFailed: '任务失败',
    shotDownTitle: '已被击落',
    shotDownSummary: '机体损毁，等待重生或手动重新接入。',
    airspaceClear: '空域已清空',
    battleGroupLost: '战斗群失守',
    crashTitle: '座机坠毁',
    crashSummary: '发生撞击，战机已损毁。',
    scoreWave: '得分 {score} · 波次 {wave}',
    changelogItemFlow: '首页与机库输入链完全隔离，昵称编辑不再被战斗输入抢占。',
    changelogItemSettings: 'Esc 现在直接打开战术设置，鼠标占用在设置界面会即时释放。',
    changelogItemHangar: '联机入口固定接入 HK 节点，并在 HUD 中显示在线玩家数量。',
    changelogItemCombat: '鼠标改为拖拽视角，滚轮负责缩放，机炮热量显示与键位提示同步修正。',
    changelogItemCombat2: '导弹链路已重做为可见实体飞行，联机双方都能看到弹道；速度与追踪性下调，更容易规避。',
    changelogItemCarrier: '航母已替换为实体模型，甲板支持舰载机待命与航母起飞，玩家昵称会保存在浏览器中。',
    changelogItemRecovery: '玩家被击落后将直接返回机库；默认昵称改为空白，已输入呼号会自动保存在浏览器中。',
    changelogItemExhaust: 'J-15 尾焰已按左右双喷口重新定位，喷焰跟随机体比例并保持两侧分离。',
    changelogItemLeaderboard: '联机战区新增实时排行榜，击落目标可得 5 分，被击落后积分会立即清零。',
    aircraftJ11Name: 'Shenyang J-15 Navy',
    aircraftJ11Tag: '舰载制空战斗机',
    aircraftJ11Desc: '重型舰载战斗机，适合航母起降、导弹拦截与持续近距空战。',
    aircraftFa18fName: 'F/A-18F Super Hornet',
    aircraftFa18fTag: '舰载多用途战斗机',
    aircraftFa18fDesc: '双座舰载机，适合航母起降、导弹拦截与中低速狗斗。',
    aircraftStatusChecking: '正在检查已挂载机体模型…',
    aircraftStatusValidating: '正在校验 {label} 模型资源…',
    aircraftStatusMounted: '{label} 模型已挂载，进入战斗后将使用真实机体。',
    aircraftStatusFallback: '{label} 模型资源不可用，当前会回退到简化机体。',
    aircraftStatusError: '{label} 模型检查失败，当前会回退到简化机体。',
    controlMouseKey: '拖拽',
    controlPitchValue: '俯仰',
    controlMissileValue: '导弹',
    controlLmbKey: '滚轮',
    controlFlareValue: '热焰弹',
    controlZoomValue: '缩放',
    brandHud: 'HFS: SKY FORCE',
    combatHintPrimary: '按住拖拽调整视角 | C 机炮 | Tab 循环目标',
    combatHintSecondary: 'Space 导弹 | F 热焰弹 | Shift 加力 | 滚轮缩放 | Esc 设置',
    lockNone: '无目标',
    lockLabel: '锁定 {percent}%',
    lockReady: '目标锁定',
    hudModePve: 'PVE RAID',
    hudModePvp: 'HK 节点',
    hostilesCount: '{count} 敌机',
    respawnStatus: '重生倒计时 {seconds}s | HK 节点 · 在线玩家 {players}',
    networkStatus: '{status} | HK 节点 · 在线玩家 {players}',
    touchFlight: '飞行',
    touchView: '视角',
    buttonMissile: '导弹',
    buttonGun: '机炮',
    buttonFlare: '热焰弹',
    buttonTarget: '目标',
    buttonTargetCycle: '锁定 / 切换',
    buttonBoost: '加力',
    buttonThrottleUp: '油门 +',
    buttonThrottleDown: '油门 -',
    buttonYawLeft: '左偏航',
    buttonYawRight: '右偏航',
    buttonPause: '暂停',
    buttonResume: '继续',
    hudFlaresValue: '热焰 {count}',
    hudGunHeatValue: '热量 {percent}%',
    gunReady: '机炮就绪',
    gunCooling: '机炮冷却 {percent}%',
    gunOverheat: '机炮过热',
    feedSoloLaunch: '{pilot} 单机出击。',
    feedRoomJoin: '{pilot} 已接入 HK 节点。',
    feedRadarTarget: '雷达指定 {target}',
    feedFlare: '热焰弹释放。',
    feedMissile: 'Fox Two，锁定 {target}',
    lockWarn: '被敌方雷达锁定，注意规避。',
    feedMpOn: 'HK 节点链路稳定。',
    feedMpOff: 'HK 节点链路断开。',
    feedReady: '系统待命，随时可以出击。',
    feedInitFailed: '初始化失败，请刷新重试。',
    targetBandit: '敌机',
    targetHp: '目标血量',
    selfHp: '本机血量',
    leaderboardTitle: '战区排行',
    leaderboardEmpty: '等待更多飞行员接入',
  },
  en: {
    settingsEyebrow: 'TACTICAL CONTROL',
    settingsTitle: 'Settings',
    reconnectNode: 'Reconnect HK Node',
    close: 'Close',
    flightControlsTitle: 'Flight Controls',
    mouseSensitivity: 'Mouse Sensitivity',
    invertYAxis: 'Invert Y Axis',
    mouseMoveKey: 'Drag Screen',
    freeLookValue: 'Look Around',
    pitchNoseValue: 'Pitch Nose',
    rollValue: 'Roll',
    yawValue: 'Yaw',
    throttleKey: 'Wheel',
    zoomValue: 'Zoom View',
    cannonKey: 'C / GUN',
    throttleAdjustValue: 'Throttle',
    afterburnerValue: 'Afterburner',
    hudTitle: 'HUD',
    hudOpacity: 'HUD Opacity',
    showFeed: 'Show Tactical Feed',
    showHints: 'Show Control Hints',
    fireMissileValue: 'Fire Missile',
    lockRequirementKey: 'Requirement',
    lockRequirementValue: 'Cycle with Tab and complete lock first',
    cannonValue: 'Cannon',
    flareValue: 'Deploy Flare',
    cycleTargetValue: 'Cycle Target',
    pauseValue: 'Pause',
    releaseNotes: 'Release Notes',
    changelogTitle: 'Changelog',
    homeEyebrow: 'Hamster Flight Simulator',
    brandTitle: 'HFS: Sky Force',
    homeIntro: 'Register your callsign, step into the hangar, and launch straight into combat.',
    pilotName: 'Pilot Name',
    enterHangar: 'Enter Hangar',
    hangarEyebrow: 'Hangar',
    hangarTitle: 'Hangar Setup',
    backHome: 'Back Home',
    multiplayerRoom: 'Node',
    multiplayerNote: 'Online sortie connects directly to the HK combat node.',
    singleplayerLaunch: 'Launch Solo',
    multiplayerLaunch: 'Launch Online',
    multiplayerConnecting: 'Linking to HK node…',
    homePlaceholder: 'Enter your callsign',
    hangarPlaceholder: 'Callsign',
    combatStandingBy: 'Combat Standing By',
    pausedEyebrow: 'Paused',
    pausedTitle: 'Regroup, then re-engage',
    resumeCombat: 'Resume Combat',
    restartSortie: 'Restart Sortie',
    missionEnd: 'Mission End',
    baseLost: 'Base Lost',
    scoreZero: 'Score 0',
    sortieAgain: 'Sortie Again',
    missionComplete: 'Mission Complete',
    missionFailed: 'Mission Failed',
    shotDownTitle: 'Shot Down',
    shotDownSummary: 'Airframe destroyed. Wait for respawn or reconnect manually.',
    airspaceClear: 'Airspace Cleared',
    battleGroupLost: 'Battle Group Lost',
    crashTitle: 'Aircraft Lost',
    crashSummary: 'Collision detected. Airframe destroyed.',
    scoreWave: 'Score {score} · Wave {wave}',
    changelogItemFlow: 'Home and hangar input are now fully isolated, so callsign editing is no longer hijacked by combat controls.',
    changelogItemSettings: 'Esc now opens tactical settings directly and releases mouse control the moment the panel appears.',
    changelogItemHangar: 'Online launch is now fixed to the HK node, with live player count shown in the HUD.',
    changelogItemCombat: 'Mouse look now uses drag, wheel now zooms, and cannon heat readouts have been clarified.',
    changelogItemCombat2: 'Missiles now fly as visible entities in multiplayer, both pilots can track their position, and their speed and seeker strength have been reduced for real evasion.',
    changelogItemCarrier: 'The carrier now uses a full model, supports deck parking and carrier launches, and pilot names are now saved in the browser.',
    changelogItemRecovery: 'Shot-down pilots now return straight to the hangar, and callsigns stay blank by default until saved in the browser.',
    changelogItemExhaust: 'J-15 exhaust has been realigned to both left and right nozzles, with separated flames that follow aircraft scale.',
    changelogItemLeaderboard: 'Online combat now includes a live leaderboard, with 5 points per kill and an immediate score reset when you are shot down.',
    aircraftJ11Name: 'Shenyang J-15 Navy',
    aircraftJ11Tag: 'Carrier Air-Superiority Fighter',
    aircraftJ11Desc: 'Heavy carrier fighter built for deck operations, missile intercepts, and sustained dogfights.',
    aircraftFa18fName: 'F/A-18F Super Hornet',
    aircraftFa18fTag: 'Carrier Multirole Fighter',
    aircraftFa18fDesc: 'Carrier-borne twin-seat fighter tuned for deck operations, missile intercepts, and slower-speed dogfights.',
    aircraftStatusChecking: 'Checking mounted aircraft model…',
    aircraftStatusValidating: 'Validating {label} model asset…',
    aircraftStatusMounted: '{label} model mounted. Live aircraft will be used in combat.',
    aircraftStatusFallback: '{label} model unavailable. Falling back to the simplified airframe.',
    aircraftStatusError: '{label} model check failed. Falling back to the simplified airframe.',
    controlMouseKey: 'Drag',
    controlPitchValue: 'Pitch',
    controlMissileValue: 'Missile',
    controlLmbKey: 'Wheel',
    controlFlareValue: 'Flare',
    controlZoomValue: 'Zoom',
    brandHud: 'HFS: SKY FORCE',
    combatHintPrimary: 'Drag to look | C cannon | Tab cycles targets',
    combatHintSecondary: 'Space missile | F flare | Shift boost | Wheel zoom | Esc settings',
    lockNone: 'NO TARGET',
    lockLabel: 'LOCK {percent}%',
    lockReady: 'TARGET LOCK',
    hudModePve: 'PVE RAID',
    hudModePvp: 'HK NODE',
    hostilesCount: '{count} HOSTILES',
    respawnStatus: 'Respawn in {seconds}s | HK Node · Players Online {players}',
    networkStatus: '{status} | HK Node · Players Online {players}',
    touchFlight: 'FLIGHT',
    touchView: 'VIEW',
    buttonMissile: 'MISSILE',
    buttonGun: 'GUN',
    buttonFlare: 'FLARE',
    buttonTarget: 'TARGET',
    buttonTargetCycle: 'LOCK / CYCLE',
    buttonBoost: 'BOOST',
    buttonThrottleUp: 'THR +',
    buttonThrottleDown: 'THR -',
    buttonYawLeft: 'YAW L',
    buttonYawRight: 'YAW R',
    buttonPause: 'PAUSE',
    buttonResume: 'RESUME',
    hudFlaresValue: 'FLR {count}',
    hudGunHeatValue: 'HEAT {percent}%',
    gunReady: 'GUN READY',
    gunCooling: 'COOLING {percent}%',
    gunOverheat: 'OVERHEAT',
    feedSoloLaunch: '{pilot} launched solo.',
    feedRoomJoin: '{pilot} linked to the HK node.',
    feedRadarTarget: 'Radar designated {target}',
    feedFlare: 'Flare deployed.',
    feedMissile: 'Fox Two, locked {target}',
    lockWarn: 'Enemy radar lock detected. Break and evade.',
    feedMpOn: 'HK node link stable.',
    feedMpOff: 'HK node link lost.',
    feedReady: 'Systems ready. Launch on command.',
    feedInitFailed: 'Initialization failed. Refresh and try again.',
    targetBandit: 'BANDIT',
    targetHp: 'TARGET HP',
    selfHp: 'SELF HP',
    leaderboardTitle: 'COMBAT RANKING',
    leaderboardEmpty: 'Waiting for more pilots to link in',
  },
} as const;
let uiLanguage: 'zh' | 'en' = loadLanguage();

type SettingsState = {
  mouseSensitivity: number;
  invertY: boolean;
  hudOpacity: number;
  showFeed: boolean;
  showHints: boolean;
};

const SETTINGS_STORAGE_KEY = 'sky-lance-settings';
const defaultSettings: SettingsState = {
  mouseSensitivity: 1,
  invertY: false,
  hudOpacity: 1,
  showFeed: true,
  showHints: true,
};
let settings: SettingsState = loadSettings();
let uiBound = false;

type AppScreen = 'home' | 'hangar' | 'battle';
let currentScreen: AppScreen = 'home';

function pushFeed(text: string, tone = 'info'): void {
  feed.unshift({ text, tone, createdAt: performance.now() });
  feed = feed.slice(0, 5);
  renderFeed();
}

function showKillBanner(text: string): void {
  killBanner.textContent = localizeFeedText(text);
  killBanner.hidden = false;
  killBanner.classList.add('visible');
  killBannerTimer = 3.2;
}

function getLeaderboardEntries(): LeaderboardEntry[] {
  if (!multiplayerMode) return [];
  const entries = multiplayer.getRemotePlayers().map((player) => ({
    id: player.id,
    name: player.name,
    score: player.score,
    self: false,
  }));
  const selfState = multiplayer.getSelfState();
  if (selfState) {
    entries.push({
      id: selfState.id,
      name: selfState.name || resolvePilotName(),
      score: selfState.score,
      self: true,
    });
  }
  return entries
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.self !== b.self) return a.self ? -1 : 1;
      return a.name.localeCompare(b.name);
    })
    .slice(0, 6);
}

function renderLeaderboard(): void {
  leaderboardList.innerHTML = '';
  const entries = getLeaderboardEntries();
  if (!entries.length) {
    const empty = document.createElement('p');
    empty.className = 'leaderboard-empty';
    empty.textContent = t('leaderboardEmpty');
    leaderboardList.appendChild(empty);
    return;
  }
  entries.forEach((entry, index) => {
    const row = document.createElement('div');
    row.className = 'leaderboard-row';
    if (entry.self) row.classList.add('is-self');

    const rank = document.createElement('span');
    rank.className = 'leaderboard-rank';
    rank.textContent = `#${index + 1}`;

    const name = document.createElement('span');
    name.className = 'leaderboard-name';
    name.textContent = entry.name || (entry.self ? resolvePilotName() : (uiLanguage === 'zh' ? '飞行员' : 'Pilot'));

    const score = document.createElement('span');
    score.className = 'leaderboard-score';
    score.textContent = `${entry.score}`;

    row.append(rank, name, score);
    leaderboardList.appendChild(row);
  });
}

function renderChat(): void {
  chatLog.innerHTML = '';
  for (const item of chatMessages) {
    const p = document.createElement('p');
    p.textContent = localizeFeedText(item.text);
    if (item.tone === 'danger') p.style.borderLeftColor = '#ff5c66';
    if (item.tone === 'success') p.style.borderLeftColor = '#63ffc7';
    chatLog.appendChild(p);
  }
  chatHint.textContent = chatTyping
    ? (uiLanguage === 'zh' ? 'Enter 发送 | Esc 关闭' : 'Enter Send | Esc Close')
    : (uiLanguage === 'zh' ? 'Enter 开始聊天' : 'Enter Chat');
  chatInput.placeholder = uiLanguage === 'zh' ? '输入战术消息' : 'Type tactical message';
  chatInput.hidden = !chatTyping;
}

function pushChat(text: string, tone = 'info'): void {
  chatMessages.unshift({ text, tone });
  chatMessages = chatMessages.slice(0, 8);
  renderChat();
}

function updateBattleInputState(): void {
  const enabled = currentScreen === 'battle'
    && !settingsScreen.classList.contains('active')
    && !pauseScreen.classList.contains('active')
    && !gameOverScreen.classList.contains('active')
    && !chatTyping;
  input.setBattleInputEnabled(enabled);
}

function setChatTyping(active: boolean): void {
  chatTyping = active && currentScreen === 'battle';
  if (chatTyping) {
    chatPanel.hidden = false;
    updateBattleInputState();
    queueMicrotask(() => chatInput.focus());
  } else {
    chatInput.blur();
    chatInput.value = '';
    updateBattleInputState();
  }
  renderChat();
}

function sendChatMessage(): void {
  const text = chatInput.value.trim();
  if (!text) {
    setChatTyping(false);
    return;
  }
  if (multiplayerMode && multiplayer.isConnected()) {
    multiplayer.sendChat(text);
  } else {
    pushChat(`[${resolvePilotName()}] ${text}`, 'info');
  }
  setChatTyping(false);
}

function loadLanguage(): 'zh' | 'en' {
  const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return saved === 'en' ? 'en' : 'zh';
}

function loadPilotName(): string {
  return (window.localStorage.getItem(PILOT_NAME_STORAGE_KEY) || '').trim();
}

function savePilotName(name: string): void {
  const normalized = name.trim();
  if (normalized) {
    window.localStorage.setItem(PILOT_NAME_STORAGE_KEY, normalized);
  } else {
    window.localStorage.removeItem(PILOT_NAME_STORAGE_KEY);
  }
}

function resolvePilotName(): string {
  return selectedPilotName.trim() || (uiLanguage === 'zh' ? '飞行员' : 'Pilot');
}

function applyLanguage(): void {
  const dict = translations[uiLanguage];
  document.documentElement.lang = uiLanguage === 'zh' ? 'zh-CN' : 'en';
  document.title = uiLanguage === 'zh'
    ? 'HFS: 天空部队 | Hamster Flight Simulator: Sky Force'
    : 'HFS: Sky Force | Hamster Flight Simulator';
  for (const element of document.querySelectorAll<HTMLElement>('[data-i18n]')) {
    const key = element.dataset.i18n as keyof typeof dict | undefined;
    if (!key) continue;
    element.textContent = dict[key];
  }
  settingsReconnectButton.textContent = t('reconnectNode');
  pauseReconnectButton.textContent = t('reconnectNode');
  const multiplayerNote = document.getElementById('multiplayer-note');
  if (multiplayerNote) multiplayerNote.textContent = t('multiplayerNote');
  homePilotNameInput.placeholder = dict.homePlaceholder;
  hangarPilotNameInput.placeholder = dict.hangarPlaceholder;
  langZhButton.classList.toggle('active', uiLanguage === 'zh');
  langEnButton.classList.toggle('active', uiLanguage === 'en');
  updateMultiplayerButtonState();
  renderFeed();
  renderChat();
  renderLeaderboard();
  refreshStaticHudLabels();
  updateHud();
  if (currentScreen === 'hangar') void refreshAircraftStatus();
  if (currentScreen !== 'battle') {
    targetName.textContent = t('targetBandit');
    if (targetHealthLabel) targetHealthLabel.textContent = t('targetHp');
    if (selfHealthLabel) selfHealthLabel.textContent = t('selfHp');
  }
}

function setLanguage(language: 'zh' | 'en'): void {
  uiLanguage = language;
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  applyLanguage();
  audio.playUiConfirm();
}

function t(key: keyof typeof translations.zh, vars?: Record<string, string | number>): string {
  let text = String(translations[uiLanguage][key]);
  if (!vars) return text;
  for (const [name, value] of Object.entries(vars)) {
    text = text.replace(`{${name}}`, String(value));
  }
  return text;
}

function localizeStatusText(status: string): string {
  if (uiLanguage === 'zh') return status;
  if (status === '战斗待命') return t('combatStandingBy');
  if (status === '守住航母战斗群') return 'Defend the carrier battle group';
  if (status === '战斗暂停') return 'Combat Paused';
  if (status === '继续拦截') return 'Resume Intercept';
  if (status === '联机交战区') return 'Online Combat Zone';
  if (status === '雷达搜索中') return 'Radar Searching';
  if (status === '座机坠毁') return 'Aircraft Destroyed';
  if (status === '座机被击落') return 'Aircraft Lost';
  if (status === '航母战斗群失守') return t('battleGroupLost');
  const waveStatus = status.match(/^第\s*(\d+)\s*波\s*\|\s*敌机\s*(\d+)$/);
  if (waveStatus) return `Wave ${waveStatus[1]} | Hostiles ${waveStatus[2]}`;
  const nextWave = status.match(/^下一波来袭\s*(\d+)s$/);
  if (nextWave) return `Next wave in ${nextWave[1]}s`;
  return status;
}

function localizeFeedText(text: string): string {
  if (uiLanguage === 'zh') return text;
  let match = text.match(/^(.+)\s单机出击。$/);
  if (match) return t('feedSoloLaunch', { pilot: match[1] });
  match = text.match(/^(.+)\s已接入 HK 节点。$/);
  if (match) return t('feedRoomJoin', { pilot: match[1] });
  match = text.match(/^雷达指定\s(.+)$/);
  if (match) return t('feedRadarTarget', { target: match[1] });
  match = text.match(/^Fox Two，锁定\s(.+)$/);
  if (match) return t('feedMissile', { target: match[1] });
  match = text.match(/^第\s*(\d+)\s*波接近，(\d+)\s*架敌机。$/);
  if (match) return `Wave ${match[1]} incoming, ${match[2]} hostiles.`;
  match = text.match(/^(.+)\s击落\s\+(\d+)$/);
  if (match) return `${match[1]} destroyed +${match[2]}`;
  if (text === 'J-11 起飞，拦截来袭机群。') return 'J-11 airborne. Intercept the incoming raid.';
  if (text === 'J-15 从航母甲板起飞，拦截来袭机群。') return 'J-15 launching from the carrier deck. Intercept the incoming raid.';
  if (text === '战斗暂停。') return 'Combat paused.';
  if (text === '恢复战斗。') return 'Back in the fight.';
  if (text === '导弹耗尽，等待下一波补给。') return 'Missiles exhausted. Await the next resupply wave.';
  if (text === '需要稳定锁定后才能发射。') return 'Stable lock required before launch.';
  if (text === '热焰弹释放。') return t('feedFlare');
  if (text === '敌机正在攻击航母战斗群。') return 'Hostiles are attacking the carrier battle group.';
  if (text === '空域暂时净空，补给完成。') return 'Airspace temporarily clear. Resupply complete.';
  if (text === '正在离开战区，自动引导返航。') return 'Leaving the combat zone. Auto-guidance returning you to the battle.';
  if (text === '海面撞击，座机坠毁。') return 'Sea impact. Aircraft destroyed.';
  if (text === '撞上航母甲板，座机坠毁。') return 'Carrier impact. Aircraft destroyed.';
  if (text === '空中相撞，座机坠毁。') return 'Mid-air collision. Aircraft destroyed.';
  if (text === 'Fox Two，导弹离架。') return 'Fox Two, missile away.';
  if (text === '导弹告警！释放热焰弹或急转脱锁。') return 'Missile warning. Drop flares or break hard to defeat the lock.';
  if (text === 'HK 节点链路稳定。') return t('feedMpOn');
  if (text === 'HK 节点链路断开。') return t('feedMpOff');
  if (text === '准备就绪：单机或联机均可起飞。') return t('feedReady');
  if (text === '初始化失败，请刷新重试。') return t('feedInitFailed');
  if (text === '香港节点接入完成。') return 'HK node link established.';
  if (text === '香港节点连接中断。') return 'HK node connection closed.';
  if (text === '香港节点连接异常。') return 'HK node connection error.';
  match = text.match(/^联机识别完成\s(.+)$/);
  if (match) return `Combat identity verified ${match[1]}.`;
  match = text.match(/^(.+)\s已脱离空域$/);
  if (match) return `${match[1]} left the combat airspace.`;
  return text;
}

function renderFeed(): void {
  eventFeed.innerHTML = '';
  for (const item of feed) {
    const p = document.createElement('p');
    p.textContent = localizeFeedText(item.text);
    p.style.borderLeftColor = item.tone === 'danger' ? '#ff5c66' : item.tone === 'success' ? '#74f29a' : '#4de7ff';
    eventFeed.appendChild(p);
  }
}

function refreshStaticHudLabels(): void {
  if (simulation.mode !== 'playing') {
    lockLabel.textContent = t('lockNone');
  }
  if (!multiplayerMode) hudMode.textContent = t('hudModePve');
  if (currentScreen !== 'battle') {
    hudTargetCount.textContent = t('hostilesCount', { count: 0 });
    hudFlares.textContent = t('hudFlaresValue', { count: simulation.player.flareAmmo });
    hudHeat.textContent = t('gunReady');
    missileButton.textContent = t('buttonMissile');
    gunButton.textContent = t('gunReady');
    flareButton.textContent = t('buttonFlare');
    targetButton.textContent = t('buttonTarget');
    boostButton.textContent = t('buttonBoost');
    throttleUpButton.textContent = t('buttonThrottleUp');
    throttleDownButton.textContent = t('buttonThrottleDown');
    pauseButton.textContent = t('buttonPause');
    targetName.textContent = t('targetBandit');
    if (targetHealthLabel) targetHealthLabel.textContent = t('targetHp');
    if (selfHealthLabel) selfHealthLabel.textContent = t('selfHp');
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>('.touch-action')) {
    const action = button.dataset.touch;
    if (action === 'yawLeft') button.textContent = t('buttonYawLeft');
    if (action === 'yawRight') button.textContent = t('buttonYawRight');
    if (action === 'throttleUp') button.textContent = t('buttonThrottleUp');
    if (action === 'throttleDown') button.textContent = t('buttonThrottleDown');
    if (action === 'fireMissile') button.textContent = t('buttonMissile');
    if (action === 'fireCannon') button.textContent = t('buttonGun');
    if (action === 'flare') button.textContent = t('buttonFlare');
    if (action === 'targetNext') button.textContent = t('buttonTarget');
    if (action === 'afterburner') button.textContent = t('buttonBoost');
    if (action === 'pause') button.textContent = t('buttonPause');
  }
}

function setScreen(screen: AppScreen): void {
  currentScreen = screen;
  homeScreen.classList.toggle('active', screen === 'home');
  hangarScreen.classList.toggle('active', screen === 'hangar');
  const battleVisible = screen === 'battle';
  if (!battleVisible && document.activeElement instanceof HTMLElement) document.activeElement.blur();
  input.setPointerLockEnabled(battleVisible);
  if (!battleVisible) chatTyping = false;
  updateBattleInputState();
  canvas.style.pointerEvents = battleVisible ? 'auto' : 'none';
  settingsButton.hidden = !battleVisible;
  leaderboardPanel.hidden = !battleVisible || !multiplayerMode;
  chatPanel.hidden = !battleVisible;
  hud.hidden = !battleVisible;
  crosshair.hidden = !battleVisible;
  targetBox.hidden = !battleVisible;
  combatDock.hidden = !battleVisible;
  touchControls.hidden = !battleVisible;
  if (!battleVisible) targetBox.classList.remove('visible');
  if (screen === 'home') queueMicrotask(() => homePilotNameInput.focus());
  if (screen === 'hangar') queueMicrotask(() => hangarPilotNameInput.focus());
}

function setSettingsVisible(visible: boolean): void {
  settingsScreen.classList.toggle('active', visible);
  if (visible) {
    resumeAfterSettingsClose = currentScreen === 'battle' && simulation.mode === 'playing';
    if (resumeAfterSettingsClose) simulation.togglePause();
  } else if (resumeAfterSettingsClose && simulation.mode === 'paused' && currentScreen === 'battle') {
    simulation.togglePause();
    resumeAfterSettingsClose = false;
  } else {
    resumeAfterSettingsClose = false;
  }
  updateBattleInputState();
}

function setPauseVisible(visible: boolean): void {
  pauseScreen.classList.toggle('active', visible);
  updateBattleInputState();
}

function setGameOverVisible(visible: boolean): void {
  gameOverScreen.classList.toggle('active', visible);
  updateBattleInputState();
}

async function startSinglePlayer(): Promise<void> {
  audio.unlock();
  multiplayerConnecting = false;
  updateMultiplayerButtonState();
  multiplayerMode = false;
  simulation.setPvpMode(false);
  multiplayer.disconnect();
  multiplayerDeathShown = false;
  setSettingsVisible(false);
  syncPilotName();
  const pilot = resolvePilotName();
  await renderer.setPlayerAircraft(selectedAircraftId);
  simulation.startGame();
  simulation.player.callsign = pilot;
  setScreen('battle');
  setPauseVisible(false);
  setGameOverVisible(false);
  pushFeed(t('feedSoloLaunch', { pilot }), 'success');
  audio.playUiConfirm();
}

async function startMultiplayer(): Promise<void> {
  if (multiplayerConnecting) return;
  audio.unlock();
  multiplayerConnecting = true;
  updateMultiplayerButtonState();
  multiplayerMode = true;
  simulation.setPvpMode(true);
  setSettingsVisible(false);
  syncPilotName();
  const pilot = resolvePilotName();
  simulation.startGame();
  simulation.player.callsign = pilot;
  setScreen('battle');
  setPauseVisible(false);
  setGameOverVisible(false);
  multiplayerDeathShown = false;
  renderLeaderboard();
  await renderer.setPlayerAircraft(selectedAircraftId);
  multiplayer.connect('HK', pilot);
  remoteTargetId = null;
  remoteLockProgress = 0;
  pushFeed(t('feedRoomJoin', { pilot }), 'success');
  audio.playUiConfirm();
}

async function reconnectMultiplayer(): Promise<void> {
  if (multiplayerConnecting) return;
  audio.unlock();
  if (!multiplayerMode) {
    await startMultiplayer();
    return;
  }
  multiplayerConnecting = true;
  updateMultiplayerButtonState();
  multiplayer.disconnect();
  syncPilotName();
  const pilot = resolvePilotName();
  multiplayer.connect('HK', pilot);
  pushFeed(t('feedRoomJoin', { pilot }), 'info');
}

function goHome(): void {
  setScreen('home');
  setSettingsVisible(false);
  setPauseVisible(false);
  setGameOverVisible(false);
  multiplayer.disconnect();
  multiplayerDeathShown = false;
  multiplayerConnecting = false;
  updateMultiplayerButtonState();
  renderLeaderboard();
  homePilotNameInput.focus();
}

function goHangar(): void {
  syncPilotName();
  setScreen('hangar');
  setSettingsVisible(false);
  setPauseVisible(false);
  setGameOverVisible(false);
  hangarPilotNameInput.focus();
}

function updateMultiplayerButtonState(): void {
  const dict = translations[uiLanguage];
  startMpButton.classList.toggle('pending', multiplayerConnecting);
  startMpButton.disabled = multiplayerConnecting;
  startMpButton.textContent = multiplayerConnecting ? dict.multiplayerConnecting : dict.multiplayerLaunch;
}

function bindPress(button: HTMLButtonElement, handler: () => void | Promise<void>): void {
  let pointerHandled = false;
  button.style.touchAction = 'manipulation';
  button.addEventListener('pointerdown', (event) => {
    pointerHandled = true;
    event.preventDefault();
    audio.unlock();
    void handler();
    queueMicrotask(() => {
      pointerHandled = false;
    });
  });
  button.addEventListener('click', (event) => {
    if (pointerHandled) {
      event.preventDefault();
      return;
    }
    audio.unlock();
    void handler();
  });
}

function bindUiEvents(): void {
  if (uiBound) return;
  uiBound = true;

  bindPress(homeContinueButton, goHangar);
  bindPress(hangarBackButton, goHome);
  bindPress(langZhButton, () => setLanguage('zh'));
  bindPress(langEnButton, () => setLanguage('en'));
  bindPress(settingsButton, () => setSettingsVisible(true));
  bindPress(settingsReconnectButton, () => reconnectMultiplayer());
  bindPress(settingsCloseButton, () => setSettingsVisible(false));
  bindPress(startButton, () => startSinglePlayer());
  bindPress(startMpButton, () => startMultiplayer());
  bindPress(resumeButton, () => {
    simulation.togglePause();
    if (simulation.mode === 'playing' && currentScreen === 'battle') input.setBattleInputEnabled(true);
  });
  bindPress(restartButton, () => startSinglePlayer());
  bindPress(pauseReconnectButton, () => reconnectMultiplayer());
  bindPress(retryButton, respawnOrRetry);
  bindPress(gameOverHomeButton, goHome);
  homePilotNameInput.addEventListener('input', () => syncPilotName('home'));
  hangarPilotNameInput.addEventListener('input', () => syncPilotName('hangar'));
  chatInput.addEventListener('keydown', (event) => {
    if (event.code === 'Enter') {
      event.preventDefault();
      sendChatMessage();
      return;
    }
    if (event.code === 'Escape') {
      event.preventDefault();
      setChatTyping(false);
    }
  });
  window.addEventListener('keydown', handleGlobalKeyDown);
  sensitivityInput.addEventListener('input', () => {
    settings.mouseSensitivity = Number(sensitivityInput.value);
    applySettings();
    saveSettings();
  });
  invertYInput.addEventListener('change', () => {
    settings.invertY = invertYInput.checked;
    applySettings();
    saveSettings();
  });
  hudOpacityInput.addEventListener('input', () => {
    settings.hudOpacity = Number(hudOpacityInput.value);
    applySettings();
    saveSettings();
  });
  showFeedInput.addEventListener('change', () => {
    settings.showFeed = showFeedInput.checked;
    applySettings();
    saveSettings();
  });
  showHintsInput.addEventListener('change', () => {
    settings.showHints = showHintsInput.checked;
    applySettings();
    saveSettings();
  });
  for (const card of aircraftCards) {
    bindPress(card, () => selectAircraft(card.dataset.aircraft || 'j15'));
  }
}

function loadSettings(): SettingsState {
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return { ...defaultSettings };
    const parsed = JSON.parse(raw) as Partial<SettingsState>;
    return {
      mouseSensitivity: Number.isFinite(parsed.mouseSensitivity) ? Number(parsed.mouseSensitivity) : defaultSettings.mouseSensitivity,
      invertY: typeof parsed.invertY === 'boolean' ? parsed.invertY : defaultSettings.invertY,
      hudOpacity: Number.isFinite(parsed.hudOpacity) ? Number(parsed.hudOpacity) : defaultSettings.hudOpacity,
      showFeed: typeof parsed.showFeed === 'boolean' ? parsed.showFeed : defaultSettings.showFeed,
      showHints: typeof parsed.showHints === 'boolean' ? parsed.showHints : defaultSettings.showHints,
    };
  } catch {
    return { ...defaultSettings };
  }
}

function saveSettings(): void {
  window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

function applySettings(): void {
  input.setMouseSensitivity(settings.mouseSensitivity);
  input.setInvertY(settings.invertY);
  hud.style.opacity = settings.hudOpacity.toFixed(2);
  eventFeed.style.display = settings.showFeed ? 'grid' : 'none';
  combatHints.hidden = !settings.showHints;

  sensitivityInput.value = settings.mouseSensitivity.toFixed(2);
  sensitivityValue.textContent = `${settings.mouseSensitivity.toFixed(2)}x`;
  invertYInput.checked = settings.invertY;
  hudOpacityInput.value = settings.hudOpacity.toFixed(2);
  hudOpacityValue.textContent = `${Math.round(settings.hudOpacity * 100)}%`;
  showFeedInput.checked = settings.showFeed;
  showHintsInput.checked = settings.showHints;
}

function syncPilotName(source?: 'home' | 'hangar'): void {
  const sourceValue = source === 'home'
    ? homePilotNameInput.value
    : source === 'hangar'
      ? hangarPilotNameInput.value
      : (hangarPilotNameInput.value || homePilotNameInput.value);
  const nextName = sourceValue.trim();
  selectedPilotName = nextName;
  homePilotNameInput.value = nextName;
  hangarPilotNameInput.value = nextName;
  savePilotName(nextName);
}

function handleGlobalKeyDown(event: KeyboardEvent): void {
  if (currentScreen !== 'battle') return;
  if (chatTyping) {
    if (event.code === 'Escape') {
      event.preventDefault();
      setChatTyping(false);
    }
    return;
  }
  if (event.target instanceof HTMLElement) {
    const tag = event.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || event.target.isContentEditable) return;
  }
  if (event.code === 'Enter') {
    event.preventDefault();
    setChatTyping(true);
    return;
  }
  if (event.code !== 'Escape') return;
  event.preventDefault();
  if (settingsScreen.classList.contains('active')) {
    setSettingsVisible(false);
    return;
  }
  setPauseVisible(false);
  setSettingsVisible(true);
}

function respawnOrRetry(): void {
  if (multiplayerMode) {
    void startMultiplayer();
    return;
  }
  void startSinglePlayer();
}

function selectAircraft(aircraftId: string): void {
  selectedAircraftId = aircraftId;
  audio.setAircraft(aircraftId);
  audio.playUiConfirm();
  for (const card of aircraftCards) {
    card.classList.toggle('active', card.dataset.aircraft === aircraftId);
  }
  void refreshAircraftStatus();
}

async function refreshAircraftStatus(): Promise<void> {
  const url = selectedAircraftId === 'fa18f' ? '/assets/aircraft/fa18f/fa18f.fbx' : '/assets/aircraft/j15/scene.gltf';
  const label = selectedAircraftId === 'fa18f' ? 'F/A-18F Super Hornet' : 'Shenyang J-15 Navy';
  hangarAircraftStatus.textContent = t('aircraftStatusValidating', { label });
  try {
    const response = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    hangarAircraftStatus.textContent = response.ok
      ? t('aircraftStatusMounted', { label })
      : t('aircraftStatusFallback', { label });
  } catch {
    hangarAircraftStatus.textContent = t('aircraftStatusError', { label });
  }
}

function handleEvents(events: SimEvent[]): void {
  audio.handleEvents(events);
  for (const event of events) {
    if (event.type === 'message') pushFeed(event.text, event.tone || 'info');
    if (event.type === 'message' && event.text.startsWith('[')) pushChat(event.text, event.tone || 'info');
    if (event.type === 'message' && (event.text.includes('导弹告警') || event.text.includes('雷达锁定'))) {
      lockWarningTimer = 2.6;
    }
    if (event.type === 'damage' && event.targetId === 'player') pulseDamage = Math.min(1, pulseDamage + event.amount / 40);
    if (event.type === 'message' && event.tone === 'success' && (event.text.includes('击落') || event.text.includes('destroyed'))) {
      showKillBanner(event.text);
    }
    if (event.type === 'game-over') {
      setSettingsVisible(false);
      setPauseVisible(false);
      setGameOverVisible(true);
      const canRespawn = event.reason === 'crash' || event.reason === 'shotdown';
      gameOverAircraftPicker.hidden = !canRespawn;
      gameOverKicker.textContent = event.victory ? t('missionComplete') : t('missionFailed');
      if (event.reason === 'crash') {
        gameOverTitle.textContent = t('crashTitle');
        gameOverSummary.textContent = t('crashSummary');
      } else if (event.reason === 'shotdown') {
        gameOverTitle.textContent = t('shotDownTitle');
        gameOverSummary.textContent = t('shotDownSummary');
      } else {
        gameOverTitle.textContent = event.victory ? t('airspaceClear') : t('battleGroupLost');
        gameOverSummary.textContent = t('scoreWave', { score: event.score, wave: event.wave });
      }
    }
  }
}

function getGunStatusText(player: typeof simulation.player): string {
  const heatPercent = Math.round(player.cannonHeat * 100);
  if (player.cannonHeat <= 0.01) return t('gunReady');
  if (player.cannonHeat >= 0.98) return t('gunOverheat');
  if (player.cannonCooldown > 0.01 || player.cannonHeat > 0.01) return t('gunCooling', { percent: heatPercent });
  return t('gunReady');
}

function updateHud(): void {
  const player = simulation.player;
  const selfState = multiplayerMode ? multiplayer.getSelfState() : null;
  const hostileCount = multiplayerMode
    ? multiplayer.getRemotePlayers().filter((remote) => remote.alive).length
    : simulation.enemies.size;
  const onlinePlayers = multiplayerMode && multiplayer.isConnected()
    ? multiplayer.getRemotePlayers().length + 1
    : 0;
  hudSpeed.textContent = `${Math.round(player.speed)}`;
  hudAltitude.textContent = `${Math.round(player.position.y)}`;
  hudHealth.textContent = `${Math.round(selfState?.health ?? player.health)}`;
  const selfHpPercent = Math.max(0, Math.min(100, selfState?.health ?? player.health));
  if (selfHealthFill) selfHealthFill.style.width = `${selfHpPercent}%`;
  hudAmmo.textContent = `${player.missileAmmo}`;
  hudFlares.textContent = t('hudFlaresValue', { count: player.flareAmmo });
  hudHeat.textContent = getGunStatusText(player);
  hudScore.textContent = `${selfState?.score ?? simulation.score}`;
  hudMode.textContent = multiplayerMode ? t('hudModePvp') : t('hudModePve');
  hudTargetCount.textContent = t('hostilesCount', { count: hostileCount });
  const respawnSeconds = selfState && !selfState.alive
    ? Math.max(0, Math.ceil((selfState.respawnAt - Date.now()) / 1000))
    : 0;
  missionStatus.textContent = multiplayerMode && multiplayer.isConnected()
    ? selfState && !selfState.alive
      ? t('respawnStatus', { seconds: respawnSeconds, players: onlinePlayers })
      : t('networkStatus', { status: localizeStatusText(simulation.statusText), players: onlinePlayers })
    : localizeStatusText(simulation.statusText);
  leaderboardPanel.hidden = currentScreen !== 'battle' || !multiplayerMode;
  renderLeaderboard();

  if (multiplayerMode && selfState && !selfState.alive && !multiplayerDeathShown) {
    multiplayerDeathShown = true;
    setSettingsVisible(false);
    setPauseVisible(false);
    setGameOverVisible(true);
    gameOverAircraftPicker.hidden = false;
    gameOverKicker.textContent = t('missionFailed');
    gameOverTitle.textContent = t('shotDownTitle');
    gameOverSummary.textContent = t('shotDownSummary');
  }
  if (multiplayerMode && selfState?.alive) {
    multiplayerDeathShown = false;
  }
  lockWarning.hidden = lockWarningTimer <= 0;
  if (!lockWarning.hidden) lockWarning.textContent = t('lockWarn');

  const activeLock = multiplayerMode ? remoteLockProgress : simulation.lock.progress;
  const lockPercent = Math.round(activeLock * 100);
  lockFill.style.width = `${lockPercent}%`;
  const hasRemoteLock = multiplayerMode ? remoteLockProgress >= 0.99 : simulation.lock.locked;
  const hasTarget = multiplayerMode ? Boolean(remoteTargetId) : Boolean(simulation.lock.targetId);
  lockLabel.textContent = hasRemoteLock ? t('lockReady') : hasTarget ? t('lockLabel', { percent: lockPercent }) : t('lockNone');
  weaponPanel.classList.toggle('locked', hasRemoteLock);
  if (!multiplayerMode) {
    if (simulation.lock.locked) autoTrackLockedTarget = true;
    if (!simulation.lock.targetId || simulation.lock.progress < 0.08) autoTrackLockedTarget = false;
    const lockTarget = simulation.getTarget();
    renderer.setAutoTrackTarget(autoTrackLockedTarget && lockTarget ? lockTarget.position : null);
  }

  pulseDamage = Math.max(0, pulseDamage - 0.04);
  damageVignette.style.opacity = `${pulseDamage * 0.9}`;
  updateControlPanel(player, selfState?.alive ?? true, hostileCount, hasRemoteLock, selfState?.flareUntil ?? 0);
}


function updateControlPanel(
  player: typeof simulation.player,
  alive: boolean,
  hostileCount: number,
  locked: boolean,
  flareUntil: number,
): void {
  const missileReady = alive && player.missileAmmo > 0 && player.missileCooldown <= 0.01 && hostileCount > 0;
  const gunReady = alive && player.cannonHeat < 0.98 && player.cannonCooldown <= 0.01;
  const flareReady = alive && player.flareAmmo > 0 && player.flareCooldown <= 0.01;
  const boostReady = alive && player.afterburnerEnergy > 0.05;

  missileButton.textContent = player.missileCooldown > 0.01 ? `MSL ${player.missileCooldown.toFixed(1)}s` : `${t('buttonMissile')} ${player.missileAmmo}`;
  gunButton.textContent = getGunStatusText(player);
  flareButton.textContent = player.flareCooldown > 0.01 ? `FLR ${player.flareCooldown.toFixed(1)}s` : `${t('buttonFlare')} ${player.flareAmmo}`;
  targetButton.textContent = locked ? t('lockReady') : hostileCount > 0 ? t('buttonTargetCycle') : t('lockNone');
  boostButton.textContent = `${t('buttonBoost')} ${Math.round(player.afterburnerEnergy * 100)}%`;
  throttleUpButton.textContent = `${t('buttonThrottleUp')} ${Math.round(player.throttle * 100)}%`;
  throttleDownButton.textContent = t('buttonThrottleDown');
  pauseButton.textContent = simulation.mode === 'paused' ? t('buttonResume') : t('buttonPause');

  setButtonState(missileButton, missileReady, !alive || player.missileAmmo <= 0, locked && missileReady);
  setButtonState(gunButton, gunReady, !alive, gunReady);
  setButtonState(flareButton, flareReady, !alive || player.flareAmmo <= 0, flareUntil > Date.now() || flareReady);
  setButtonState(targetButton, hostileCount > 0, hostileCount === 0, hostileCount > 0);
  setButtonState(boostButton, boostReady, !alive, player.afterburnerEnergy > 0.1);
  setButtonState(throttleUpButton, alive, !alive, player.throttle > 0.72);
  setButtonState(throttleDownButton, alive, !alive, player.throttle < 0.58);
  setButtonState(pauseButton, true, false, simulation.mode === 'paused');
}

function setButtonState(button: HTMLButtonElement, ready: boolean, disabled: boolean, active: boolean): void {
  button.classList.toggle('ready', ready);
  button.classList.toggle('disabled', disabled);
  button.classList.toggle('active', active);
  button.classList.toggle('hot', button === gunButton && simulation.player.cannonHeat > 0.84);
  button.disabled = disabled;
  syncTouchActionState(button, ready, disabled, active, button === gunButton && simulation.player.cannonHeat > 0.84);
}

function syncTouchActionState(
  sourceButton: HTMLButtonElement,
  ready: boolean,
  disabled: boolean,
  active: boolean,
  hot: boolean,
): void {
  const action = sourceButton.dataset.touch;
  if (!action) return;
  for (const button of document.querySelectorAll<HTMLButtonElement>(`.touch-action[data-touch="${action}"]`)) {
    button.classList.toggle('ready', ready);
    button.classList.toggle('disabled', disabled);
    button.classList.toggle('active', active);
    button.classList.toggle('hot', hot);
    button.disabled = disabled;
  }
}

function updateTargetBox(): void {
  const target = multiplayerMode
    ? multiplayer.getRemotePlayers().find((player) => player.id === remoteTargetId && player.alive) ?? null
    : simulation.getTarget();
  if (!target) {
    targetBox.classList.remove('visible');
    if (targetHealthWrap) targetHealthWrap.hidden = true;
    renderer.setAutoTrackTarget(null);
    autoTrackLockedTarget = false;
    return;
  }
  const projected = renderer.projectWorldToScreen(target.position);
  if (!projected.visible) {
    targetBox.classList.remove('visible');
    if (targetHealthWrap) targetHealthWrap.hidden = true;
    return;
  }
  targetBox.classList.add('visible');
  targetBox.style.transform = `translate3d(${projected.x}px, ${projected.y}px, 0)`;
  targetName.textContent = 'callsign' in target ? target.callsign : target.name || t('targetBandit');
  targetRange.textContent = `${formatMeters(target.position.distanceTo(simulation.player.position))}m`;
  const hp = Math.max(0, Math.min(100, 'maxHealth' in target ? (target.health / Math.max(1, target.maxHealth)) * 100 : target.health));
  if (targetHealthWrap) targetHealthWrap.hidden = false;
  if (targetHealthLabel) targetHealthLabel.textContent = t('targetHp');
  if (targetHealthFill) targetHealthFill.style.width = `${hp}%`;
}

function updateMultiplayerTargeting(controls: ReturnType<InputController['consumeFrame']>, dt: number): void {
  if (!multiplayerMode) return;
  const remotes = multiplayer.getRemotePlayers().filter((player) => player.alive);
  if (!remotes.length) {
    remoteTargetId = null;
    remoteLockProgress = 0;
    autoTrackLockedTarget = false;
    renderer.setAutoTrackTarget(null);
    return;
  }

  remotes.sort((a, b) => a.position.distanceToSquared(simulation.player.position) - b.position.distanceToSquared(simulation.player.position));

  if (controls.targetNext) {
    const currentIndex = remotes.findIndex((player) => player.id === remoteTargetId);
    const next = remotes[(currentIndex + 1 + remotes.length) % remotes.length];
    remoteTargetId = next?.id ?? remotes[0]?.id ?? null;
    if (next) pushFeed(t('feedRadarTarget', { target: next.name }), 'info');
    autoTrackLockedTarget = false;
  }

  const forward = simulation.getPlayerForward(new THREE.Vector3());
  let target = remotes.find((player) => player.id === remoteTargetId) ?? null;
  if (!target) target = remotes[0] ?? null;
  if (!target) {
    remoteLockProgress = 0;
    return;
  }

  const toTarget = target.position.clone().sub(simulation.player.position);
  const range = toTarget.length();
  const dot = forward.dot(toTarget.normalize());
  remoteTargetId = target.id;
  const inCone = range < 2600 && dot > Math.cos(THREE.MathUtils.degToRad(34));
  if (inCone) {
    remoteLockProgress = Math.min(1, remoteLockProgress + dt / 1.1);
  } else {
    remoteLockProgress = Math.max(0, remoteLockProgress - dt * 1.2);
  }
  if (remoteLockProgress >= 0.99) autoTrackLockedTarget = true;
  if (remoteLockProgress < 0.08) autoTrackLockedTarget = false;
  renderer.setAutoTrackTarget(autoTrackLockedTarget ? target.position : null);
}

function handleMultiplayerCombat(controls: ReturnType<InputController['consumeFrame']>): SimEvent[] {
  const localEvents: SimEvent[] = [];
  if (!multiplayerMode || !multiplayer.isConnected()) return localEvents;
  const selfState = multiplayer.getSelfState();
  if (selfState && !selfState.alive) return localEvents;

  const target = multiplayer.getRemotePlayers().find((player) => player.id === remoteTargetId && player.alive) ?? null;
  if (controls.deployFlare && simulation.player.flareAmmo > 0 && simulation.player.flareCooldown <= 0) {
    simulation.player.flareAmmo -= 1;
    simulation.player.flareCooldown = 1.1;
    multiplayer.sendCombat('flare');
    pushFeed(t('feedFlare'), 'info');
  }

  if (controls.fireMissile && target && remoteLockProgress >= 0.99 && simulation.player.missileAmmo > 0 && simulation.player.missileCooldown <= 0) {
    simulation.player.missileAmmo -= 1;
    simulation.player.missileCooldown = 0.95;
    const forward = simulation.getPlayerForward(new THREE.Vector3());
    const from = simulation.player.position.clone().addScaledVector(forward, 18);
    localEvents.push({ type: 'explosion', position: from.clone(), radius: 10, tone: 'small' });
    multiplayer.sendCombat('missile', target.id);
    pushFeed(t('feedMissile', { target: target.name }), 'success');
  }

  if (controls.fireCannon && simulation.player.cannonCooldown <= 0 && simulation.player.cannonHeat < 0.98) {
    simulation.player.cannonCooldown = 0.08;
    simulation.player.cannonHeat = Math.min(1, simulation.player.cannonHeat + 0.065);
    localEvents.push(...simulation.emitPlayerCannonBurst());

    if (target) {
      const forward = simulation.getPlayerForward(new THREE.Vector3());
      const toTarget = target.position.clone().sub(simulation.player.position);
      const range = toTarget.length();
      const dot = forward.dot(toTarget.normalize());
      if (range < 760 && dot > 0.992) {
        multiplayer.sendCombat('cannon', target.id);
      }
    }
  }
  return localEvents;
}

function drawRadar(): void {
  const w = radarCanvas.width;
  const h = radarCanvas.height;
  const cx = w * 0.5;
  const cy = h * 0.5;
  const r = Math.min(w, h) * 0.45;

  radarCtx.clearRect(0, 0, w, h);
  radarCtx.fillStyle = 'rgba(4, 12, 17, 0.95)';
  radarCtx.beginPath();
  radarCtx.arc(cx, cy, r, 0, Math.PI * 2);
  radarCtx.fill();
  radarCtx.strokeStyle = 'rgba(77, 231, 255, 0.4)';
  radarCtx.lineWidth = 1;
  radarCtx.beginPath();
  radarCtx.arc(cx, cy, r, 0, Math.PI * 2);
  radarCtx.stroke();

  const forward = simulation.getPlayerForward(new THREE.Vector3());
  const heading = Math.atan2(forward.x, forward.z);
  const drawPoint = (position: THREE.Vector3, color: string, size: number): void => {
    const rel = position.clone().sub(simulation.player.position);
    const maxRange = 3200;
    rel.y = 0;
    const distance = rel.length();
    if (distance > maxRange) return;
    const angle = Math.atan2(rel.x, rel.z) - heading;
    const rr = (distance / maxRange) * r;
    const x = cx + Math.sin(angle) * rr;
    const y = cy + Math.cos(angle) * rr;
    radarCtx.fillStyle = color;
    radarCtx.beginPath();
    radarCtx.arc(x, y, size, 0, Math.PI * 2);
    radarCtx.fill();
  };

  drawPoint(simulation.basePosition, '#74f29a', 3.2);
  for (const enemy of simulation.enemies.values()) drawPoint(enemy.position, '#ff5c66', 2.6);
  for (const remote of multiplayer.getRemotePlayers()) drawPoint(remote.position, '#7dffa8', 2.4);
  for (const missile of multiplayer.getMissiles()) drawPoint(missile.position, missile.faction === 'enemy' ? '#ffb06f' : '#7df4ff', 1.8);

  radarCtx.fillStyle = '#4de7ff';
  radarCtx.beginPath();
  radarCtx.moveTo(cx, cy - 6);
  radarCtx.lineTo(cx - 4, cy + 4);
  radarCtx.lineTo(cx + 4, cy + 4);
  radarCtx.closePath();
  radarCtx.fill();
}

function animate(now: number): void {
  const dt = Math.min(0.04, (now - lastTime) / 1000);
  lastTime = now;
  lockWarningTimer = Math.max(0, lockWarningTimer - dt);
  killBannerTimer = Math.max(0, killBannerTimer - dt);
  if (killBannerTimer <= 0) {
    killBanner.classList.remove('visible');
    killBanner.hidden = true;
  }

  const look = input.consumeLookDelta();
  renderer.applyCameraLook(look.yaw, look.pitch);
  renderer.applyCameraZoom(input.consumeZoomDelta());
  const controls = input.consumeFrame();
  if (controls.targetNext) {
    autoTrackLockedTarget = false;
    renderer.setAutoTrackTarget(null);
  }
  const events = simulation.step(dt, controls);
  updateMultiplayerTargeting(controls, dt);
  events.push(...handleMultiplayerCombat(controls));
  if (pendingNetworkEvents.length > 0) {
    events.push(...pendingNetworkEvents);
    pendingNetworkEvents = [];
  }
  handleEvents(events);
  updateHud();
  updateTargetBox();
  drawRadar();
  audio.update({
    screen: currentScreen,
    paused: simulation.mode === 'paused' || settingsScreen.classList.contains('active') || pauseScreen.classList.contains('active') || gameOverScreen.classList.contains('active'),
    throttle: simulation.player.throttle,
    afterburner: controls.afterburner ? 1 : Math.max(0, simulation.player.afterburnerEnergy - 0.72),
    speed: simulation.player.speed,
    lockWarningActive: lockWarningTimer > 0,
  });

  if (simulation.mode === 'paused') {
    setPauseVisible(!settingsScreen.classList.contains('active'));
  }
  if (simulation.mode !== 'paused' || settingsScreen.classList.contains('active')) setPauseVisible(false);

  if (currentScreen === 'battle' && multiplayerMode && simulation.mode === 'playing' && multiplayer.isConnected()) {
    const selfState = multiplayer.getSelfState();
    if (selfState) simulation.player.health = selfState.health;
    stateSendCooldown -= dt;
    if (stateSendCooldown <= 0) {
      multiplayer.sendState(simulation.player.position, simulation.player.quaternion, simulation.player.throttle);
      stateSendCooldown = 1 / 20;
    }
    const remotePlayers = multiplayer.getRemotePlayers();
    renderer.syncRemotePlayers(remotePlayers.map((remote) => ({
      ...remote,
      position: remote.position.clone(),
      quaternion: remote.quaternion.clone(),
    })));
    const remoteMissiles = multiplayer.getMissiles();
    renderer.syncRemoteMissiles(remoteMissiles.map((missile: RemoteMissileState) => ({
      ...missile,
      position: missile.position.clone(),
      direction: missile.direction.clone(),
    })));
  } else {
    renderer.syncRemotePlayers([]);
    renderer.syncRemoteMissiles([]);
  }

  renderer.render(dt, events);
  requestAnimationFrame(animate);
}

async function boot(): Promise<void> {
  bindUiEvents();
  input.setPointerLockEnabled(false);
  updateBattleInputState();
  canvas.style.pointerEvents = 'none';
  window.addEventListener('pointerdown', () => audio.unlock(), { passive: true });
  window.addEventListener('keydown', () => audio.unlock(), { passive: true });
  selectedPilotName = loadPilotName();
  homePilotNameInput.value = selectedPilotName;
  hangarPilotNameInput.value = selectedPilotName;
  applySettings();
  applyLanguage();
  updateMultiplayerButtonState();
  selectAircraft('j15');
  gameOverAircraftPicker.hidden = true;
  void refreshAircraftStatus();
  setScreen('home');

  await renderer.init();
  multiplayer.onConnection((connected) => pushFeed(
    connected ? t('feedMpOn') : t('feedMpOff'),
    connected ? 'success' : 'warn',
  ));
  multiplayer.onConnection((connected) => {
    multiplayerConnecting = false;
    updateMultiplayerButtonState();
    if (!connected && currentScreen !== 'battle') setScreen('hangar');
  });
  multiplayer.onMessage((message) => pushFeed(message, 'info'));
  multiplayer.onMessage((message) => {
    if (message.includes('错误') || message.includes('断开') || message.includes('异常') || message.includes('中断')) {
      multiplayerConnecting = false;
      updateMultiplayerButtonState();
    }
  });
  multiplayer.onEvent((event) => {
    const selfId = multiplayer.getSelfState()?.id ?? null;
    if (event.chat && typeof event.text === 'string') pushChat(event.text, event.tone || 'info');
    if (event.damage && event.targetId === multiplayer.getSelfState()?.id) {
      pulseDamage = Math.min(1, pulseDamage + event.damage / 40);
      if (event.weapon === 'missile') {
        pendingNetworkEvents.push({
          type: 'explosion',
          position: simulation.player.position.clone(),
          radius: 18,
          tone: 'large',
        });
      }
    }
    if (event.kill && typeof event.text === 'string') showKillBanner(event.text);
    if (event.weapon === 'missile' && event.launch && event.targetId === multiplayer.getSelfState()?.id) {
      lockWarningTimer = 2.8;
      pushFeed(t('lockWarn'), 'danger');
    }
    if (event.weapon === 'missile' && event.splash && event.position) {
      pendingNetworkEvents.push({
        type: 'explosion',
        position: new THREE.Vector3(event.position.x ?? 0, event.position.y ?? 0, event.position.z ?? 0),
        radius: 14,
        tone: 'small',
      });
    }
    if (event.weapon === 'cannon' && event.tracer && event.from && event.to && event.attackerId !== selfId) {
      pendingNetworkEvents.push({
        type: 'gun-tracer',
        from: new THREE.Vector3(event.from.x ?? 0, event.from.y ?? 0, event.from.z ?? 0),
        to: new THREE.Vector3(event.to.x ?? 0, event.to.y ?? 0, event.to.z ?? 0),
        hit: Boolean(event.hit),
        faction: event.faction === 'player' ? 'player' : 'enemy',
      });
    }
    if (event.respawn) {
      simulation.player.position.set(event.position.x, event.position.y, event.position.z);
      simulation.player.quaternion.set(event.quaternion.x, event.quaternion.y, event.quaternion.z, event.quaternion.w);
      simulation.player.health = 100;
    }
  });

  pushFeed(t('feedReady'), 'success');
  requestAnimationFrame(animate);
}

boot().catch((error) => {
  console.error(error);
  pushFeed(t('feedInitFailed'), 'danger');
});
